import { useCallback, useState } from "react";
import type { SucursalMin } from "./tipos";

// La botica en la que el DUEÑO está atendiendo en el Mostrador (Kevin, 8-oct-2026: «para no manejar dos
// cuentas»). La cuenta del dueño no tiene botica propia —la base lo exige— así que la elige él y este
// equipo la recuerda. Viaja al servidor en el header X-Botica, que la verifica contra su cadena.
//
// Dos reglas que sostienen la separación de inventarios:
//   1. El header solo se manda desde el Mostrador y la Recepción (`boticaDelMostrador`, que App fija al
//      pintar esas pantallas). En el panel cada pantalla trae su propio selector y su ?sucursal_id; si el
//      header se colara ahí, una pantalla que olvidara su botica caería en silencio en la del Mostrador.
//   2. Cada operación de la cola guarda la botica en la que se hizo (cola.ts). Si el dueño cambia de botica
//      con ventas sin enviar, esas ventas llegan a la botica donde se cobraron, no a la nueva.

const clave = (usuarioId: string) => `huayruro.botica-trabajo.${usuarioId}`;

function leer(usuarioId: string | null): SucursalMin | null {
  if (!usuarioId) return null;
  try {
    const crudo = window.localStorage.getItem(clave(usuarioId));
    if (!crudo) return null;
    const v = JSON.parse(crudo) as Partial<SucursalMin>;
    return typeof v.id === "string" && typeof v.nombre === "string" ? { id: v.id, nombre: v.nombre, direccion: v.direccion ?? null } : null;
  } catch {
    return null;
  }
}

// Solo para el dueño: los demás roles tienen su botica en la cuenta y nunca la eligen.
export function useBoticaTrabajo(usuarioId: string | null, esDueno: boolean): [SucursalMin | null, (b: SucursalMin | null) => void] {
  const [botica, setBotica] = useState<SucursalMin | null>(() => (esDueno ? leer(usuarioId) : null));
  const [deQuien, setDeQuien] = useState(usuarioId);
  // Otra cuenta en el mismo equipo: se lee la suya (se ajusta en el render, sin un efecto de por medio).
  if (deQuien !== usuarioId) {
    setDeQuien(usuarioId);
    setBotica(esDueno ? leer(usuarioId) : null);
  }
  const elegir = useCallback(
    (b: SucursalMin | null) => {
      setBotica(b);
      if (!usuarioId) return;
      try {
        if (b) window.localStorage.setItem(clave(usuarioId), JSON.stringify(b));
        else window.localStorage.removeItem(clave(usuarioId));
      } catch {
        /* sin almacenamiento (ventana privada): vale para esta sesión del navegador */
      }
    },
    [usuarioId],
  );
  return [esDueno ? botica : null, elegir];
}

// El header de las pantallas del Mostrador. Lo fija App AL PINTAR (no en un efecto: los efectos de los
// hijos corren antes que los del padre, y sus primeras lecturas saldrían sin botica).
let boticaMostrador: string | null = null;
export const getBoticaMostrador = (): string | null => boticaMostrador;
export const setBoticaMostrador = (id: string | null): void => {
  boticaMostrador = id;
};
